import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BoltOutlined as BoltOutlinedIcon,
  DateRangeOutlined as DateRangeOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  LockOutlined as LockOutlinedIcon,
  PlayArrowOutlined as PlayArrowOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  PersonOffOutlined as PersonOffOutlinedIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import {
  PROGRAMA_NOMINA,
  puedeEditarProgramaNomina,
  puedeVerModuloNomina,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import IniciarNomina, { puedeUsarIniciarNomina as puedeVerAccionIniciarNomina } from './IniciarNomina';
import DefinicionNomina, {
  puedeUsarDefinicionNomina as puedeVerAccionDefinicionNomina,
} from './DefinicionNomina';
import AplicarDescuentos, {
  puedeUsarAplicarDescuentos as puedeVerAccionAplicarDescuentos,
} from './AplicarDescuentos';
import ExcluirEmpleadoNomina, {
  puedeUsarExcluirEmpleados as puedeVerAccionExcluirEmpleados,
} from './ExcluirEmpleadoNomina';
import CerrarNomina, {
  puedeUsarCerrarNomina as puedeVerAccionCerrarNomina,
} from './CerrarNomina';

export const PROGRAMA_CONFIGURACION_NOMINA = 'configuracionnomina';
export const PROGRAMA_NOMINA_INICIAL = 'nominainicial';

const HORA_RE = /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;

const PERIODICIDADES = [
  { value: 'Mensual', label: 'Mensual' },
  { value: 'Quincenal', label: 'Quincenal' },
];

const valoresVacios = {
  horaentrada: '',
  horatardanza: '',
  inicioalmuerzo: '',
  finalalmuerzo: '',
  ljhorasalida: '',
  vhorasalida: '',
  ljextras: '',
  vextras: '',
  afp: '',
  sfs: '',
  periodicidad: 'Mensual',
};

const CAMPOS_JORNADA = [
  { campo: 'horaentrada', label: 'Hora de entrada', desc: 'Inicio de la jornada laboral' },
  { campo: 'horatardanza', label: 'Hora tardanza', desc: 'A partir de esta hora se marca tardanza' },
  { campo: 'inicioalmuerzo', label: 'Inicio almuerzo', desc: 'Hora en que comienza el almuerzo' },
  { campo: 'finalalmuerzo', label: 'Final almuerzo', desc: 'Hora en que termina el almuerzo' },
  { campo: 'ljhorasalida', label: 'L-J hora salida', desc: 'Salida de lunes a jueves' },
  { campo: 'vhorasalida', label: 'Viernes hora salida', desc: 'Salida del viernes' },
];

const CAMPOS_EXTRAS = [
  { campo: 'ljextras', label: 'L-J extras a partir de', desc: 'Horas extras de lunes a jueves' },
  { campo: 'vextras', label: 'Viernes extras a partir de', desc: 'Horas extras del viernes' },
];

const CAMPOS_APORTES = [
  { campo: 'afp', label: '% AFP', desc: 'Porcentaje de AFP del empleado', tipo: 'porcentaje' },
  { campo: 'sfs', label: '% SFS', desc: 'Porcentaje de SFS del empleado', tipo: 'porcentaje' },
];

const CAMPOS_HORA = [...CAMPOS_JORNADA, ...CAMPOS_EXTRAS];

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
  height: '100%',
};

const btnPrimarySx = {
  height: 38,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
};

const btnAccionesSx = (abierto) => ({
  height: 38,
  px: 1.7,
  color: FP.color.navy,
  bgcolor: abierto ? FP.color.accentBlueSoft : '#FFFFFF',
  border: `1px solid ${abierto ? FP.color.navy : FP.color.borderStrong}`,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.82rem',
  letterSpacing: '0.02em',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: FP.color.accentBlueSoft,
    border: `1px solid ${FP.color.navy}`,
    boxShadow: 'none',
  },
});

const tituloSeccionSx = {
  fontWeight: 800,
  color: FP.color.navy,
  fontSize: '0.72rem',
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  mb: 2,
};

/** Acepta solo dígitos de HH:MM:SS (00–23 : 00–59 : 00–59) mientras se escribe. */
export function enmascararHora(valor) {
  const crudo = String(valor ?? '').replace(/\D/g, '');
  const out = [];
  for (let i = 0; i < crudo.length; i += 1) {
    const d = crudo[i];
    const pos = out.length;
    if (pos >= 6) break;
    if (pos === 0) {
      if (d > '2') {
        out.push('0', d);
      } else {
        out.push(d);
      }
      continue;
    }
    if (pos === 1) {
      if (out[0] === '2' && d > '3') continue;
      out.push(d);
      continue;
    }
    if (pos === 2 || pos === 4) {
      if (d > '5') continue;
      out.push(d);
      continue;
    }
    out.push(d);
  }
  const digits = out.slice(0, 6).join('');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4)}`;
}

function normalizarHora(valor) {
  const texto = enmascararHora(valor);
  if (/^\d{2}:\d{2}$/.test(texto)) return `${texto}:00`;
  return texto;
}

function validarHoraLocal(valor, etiqueta) {
  const hora = normalizarHora(valor);
  if (!hora) return `La ${etiqueta} no puede estar en blanco`;
  if (!HORA_RE.test(hora)) return `Incorrecto valor en ${etiqueta}`;
  return '';
}

function validarPorcentajeLocal(valor, etiqueta) {
  const texto = String(valor || '').trim().replace(',', '.');
  if (!texto) return `El valor en ${etiqueta} no puede estar en blanco`;
  const numero = Number(texto);
  if (Number.isNaN(numero) || numero < 0 || numero > 100) {
    return `Incorrecto valor en ${etiqueta}`;
  }
  return '';
}

/**
 * Configuración inicial de nómina. Horarios y aportes AFP/SFS.
 */
const ConfiguracionNomina = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles, roleWeb, nombreNegocio } = controller;
  const { post } = useApi();

  const puedeLeer = useMemo(
    () =>
      puedeVerModuloNomina(roleDetalles, roleWeb) ||
      puedeEditarProgramaNomina(roleDetalles, PROGRAMA_CONFIGURACION_NOMINA, roleWeb) ||
      puedeEditarProgramaNomina(roleDetalles, PROGRAMA_NOMINA_INICIAL, roleWeb),
    [roleDetalles, roleWeb]
  );
  const puedeModificar = useMemo(
    () =>
      puedeEditarProgramaNomina(roleDetalles, PROGRAMA_CONFIGURACION_NOMINA, roleWeb) ||
      puedeEditarProgramaNomina(roleDetalles, PROGRAMA_NOMINA_INICIAL, roleWeb) ||
      puedeEditarProgramaNomina(roleDetalles, PROGRAMA_NOMINA, roleWeb),
    [roleDetalles, roleWeb]
  );

  const puedeIniciarNomina = useMemo(
    () => puedeVerAccionIniciarNomina(roleDetalles),
    [roleDetalles]
  );
  const puedeDefinirNomina = useMemo(
    () => puedeVerAccionDefinicionNomina(roleDetalles),
    [roleDetalles]
  );
  const puedeAplicarDescuentos = useMemo(
    () => puedeVerAccionAplicarDescuentos(roleDetalles),
    [roleDetalles]
  );
  const puedeExcluirEmpleados = useMemo(
    () => puedeVerAccionExcluirEmpleados(roleDetalles),
    [roleDetalles]
  );
  const puedeCerrarNomina = useMemo(
    () => puedeVerAccionCerrarNomina(roleDetalles),
    [roleDetalles]
  );
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [formulario, setFormulario] = useState(valoresVacios);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [menuAcciones, setMenuAcciones] = useState(null);
  const [accionActiva, setAccionActiva] = useState(null);

  const opcionesAcciones = useMemo(
    () =>
      [
        {
          id: 'iniciarnomina',
          label: 'Inicio Nómina',
          icon: <PlayArrowOutlinedIcon fontSize="small" />,
          visible: puedeIniciarNomina,
        },
        {
          id: 'definicionnomina',
          label: 'Definición de nómina',
          icon: <DateRangeOutlinedIcon fontSize="small" />,
          visible: puedeDefinirNomina,
        },
        {
          id: 'aplicardescuentos',
          label: 'Aplicar Descuentos',
          icon: <ReceiptLongOutlinedIcon fontSize="small" />,
          visible: puedeAplicarDescuentos,
        },
        {
          id: 'excluirempleados',
          label: 'Excluir empleados',
          icon: <PersonOffOutlinedIcon fontSize="small" />,
          visible: puedeExcluirEmpleados,
        },
        {
          id: 'cerrarnomina',
          label: 'Cerrar nómina',
          icon: <LockOutlinedIcon fontSize="small" />,
          visible: puedeCerrarNomina,
        },
      ].filter((op) => op.visible),
    [puedeIniciarNomina, puedeDefinirNomina, puedeAplicarDescuentos, puedeExcluirEmpleados, puedeCerrarNomina]
  );

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany]
  );

  const cargarConfiguracion = useCallback(async () => {
    if (!puedeLeer || !puedeOperar) {
      setFormulario(valoresVacios);
      return;
    }
    setCargando(true);
    try {
      const { ok, data } = await post('/api/nomina_obtenerconfiguracion', credencialesEmpresa());
      if (!ok) {
        throw new Error(data.Error || 'Error al cargar la configuración de nómina');
      }
      const cfg = data.configuracion || {};
      setFormulario({
        ...valoresVacios,
        horaentrada: enmascararHora(cfg.horaentrada || ''),
        horatardanza: enmascararHora(cfg.horatardanza || ''),
        inicioalmuerzo: enmascararHora(cfg.inicioalmuerzo || ''),
        finalalmuerzo: enmascararHora(cfg.finalalmuerzo || ''),
        ljhorasalida: enmascararHora(cfg.ljhorasalida || ''),
        vhorasalida: enmascararHora(cfg.vhorasalida || ''),
        ljextras: enmascararHora(cfg.ljextras || ''),
        vextras: enmascararHora(cfg.vextras || ''),
        afp: cfg.afp || '',
        sfs: cfg.sfs || '',
        periodicidad: cfg.periodicidad === 'Quincenal' ? 'Quincenal' : 'Mensual',
      });
    } catch (e) {
      showError(e.message || 'Error al cargar la configuración de nómina');
    } finally {
      setCargando(false);
    }
  }, [puedeLeer, puedeOperar, post, credencialesEmpresa, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperar) cargarConfiguracion();
  }, [puedeLeer, puedeOperar, cargarConfiguracion]);

  const setCampo = (campo) => (evento) => {
    const valor = evento?.target?.value ?? '';
    setFormulario((prev) => ({ ...prev, [campo]: valor }));
  };

  const setCampoHora = (campo) => (evento) => {
    const valor = enmascararHora(evento?.target?.value ?? '');
    setFormulario((prev) => ({ ...prev, [campo]: valor }));
  };

  const guardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para guardar la configuración.');
      return;
    }
    for (const item of CAMPOS_HORA) {
      const error = validarHoraLocal(formulario[item.campo], item.label.toLowerCase());
      if (error) {
        showError(error);
        return;
      }
    }
    const errorAfp = validarPorcentajeLocal(formulario.afp, 'AFP');
    if (errorAfp) {
      showError(errorAfp);
      return;
    }
    const errorSfs = validarPorcentajeLocal(formulario.sfs, 'SFS');
    if (errorSfs) {
      showError(errorSfs);
      return;
    }
    if (formulario.periodicidad !== 'Mensual' && formulario.periodicidad !== 'Quincenal') {
      showError('La periodicidad debe ser Mensual o Quincenal');
      return;
    }

    setGuardando(true);
    try {
      const payload = { ...credencialesEmpresa() };
      CAMPOS_HORA.forEach((item) => {
        payload[item.campo] = normalizarHora(formulario[item.campo]);
      });
      payload.afp = String(formulario.afp).trim().replace(',', '.');
      payload.sfs = String(formulario.sfs).trim().replace(',', '.');
      payload.periodicidad = formulario.periodicidad;

      const { ok, data } = await post('/api/nomina_guardarconfiguracion', payload);
      if (!ok) {
        throw new Error(data.Error || 'No se pueden grabar los datos');
      }
      showSuccess(data.ok || 'Datos guardados exitosamente');
      cargarConfiguracion();
    } catch (e) {
      showError(e.message || 'No se pueden grabar los datos');
    } finally {
      setGuardando(false);
    }
  };

  const campoHora = (item) => (
    <TextField
      fullWidth
      label={item.label}
      helperText={item.desc}
      placeholder="00:00:00"
      value={formulario[item.campo]}
      onChange={setCampoHora(item.campo)}
      onBlur={() =>
        setFormulario((prev) => ({
          ...prev,
          [item.campo]: normalizarHora(prev[item.campo]),
        }))
      }
      inputProps={{
        maxLength: 8,
        inputMode: 'numeric',
        pattern: '[0-2][0-9]:[0-5][0-9]:[0-5][0-9]',
        autoComplete: 'off',
      }}
      disabled={!puedeModificar}
      sx={fpInputSx}
    />
  );

  const campoPorcentaje = (item) => (
    <TextField
      fullWidth
      label={item.label}
      helperText={item.desc}
      placeholder="0.00"
      value={formulario[item.campo]}
      onChange={setCampo(item.campo)}
      inputProps={{ maxLength: 8, inputMode: 'decimal' }}
      disabled={!puedeModificar}
      sx={fpInputSx}
    />
  );

  const renderAcceso = (mensaje) => (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Configuración"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
            {mensaje}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  if (!puedeLeer) {
    return renderAcceso('No tienes permisos para acceder a la configuración de nómina.');
  }

  if (!puedeOperar) {
    return renderAcceso('Seleccione una empresa para configurar la nómina.');
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Configuración"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Horarios, aportes y periodicidad`
            : 'Horarios, aportes y periodicidad'
        }
        cta={
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<BoltOutlinedIcon sx={{ fontSize: '1.05rem !important' }} />}
              endIcon={
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: '1.1rem !important',
                    transition: 'transform 0.2s ease',
                    transform: menuAcciones ? 'rotate(180deg)' : 'none',
                  }}
                />
              }
              onClick={(e) => setMenuAcciones(e.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={Boolean(menuAcciones) ? 'true' : undefined}
              sx={btnAccionesSx(Boolean(menuAcciones))}
            >
              Acciones
            </Button>
            <Menu
              anchorEl={menuAcciones}
              open={Boolean(menuAcciones)}
              onClose={() => setMenuAcciones(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              transitionDuration={160}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1,
                  minWidth: 248,
                  borderRadius: 2.2,
                  border: `1px solid ${FP.color.border}`,
                  boxShadow: FP.shadow.md,
                  overflow: 'hidden',
                },
              }}
              MenuListProps={{ sx: { py: 0.75 } }}
            >
              {opcionesAcciones.length === 0 ? (
                <MenuItem disabled sx={{ fontWeight: 600, color: FP.color.textMuted, mx: 0.75 }}>
                  No hay acciones disponibles
                </MenuItem>
              ) : (
                opcionesAcciones.map((op, idx) => (
                  <Box key={op.id}>
                    {idx > 0 ? (
                      <Divider sx={{ mx: 1.5, borderColor: 'transparent', bgcolor: 'rgba(15,23,42,0.08)' }} />
                    ) : null}
                    <MenuItem
                      onClick={() => {
                        setMenuAcciones(null);
                        setAccionActiva(op.id);
                      }}
                      sx={{
                        mx: 0.75,
                        my: 0.35,
                        py: 1.05,
                        px: 1.2,
                        borderRadius: 1.6,
                        fontWeight: 700,
                        color: FP.color.textPrimary,
                        gap: 1.2,
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: 1.2,
                          bgcolor: FP.color.accentBlueSoft,
                          color: FP.color.accentBlue,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {op.icon}
                      </Box>
                      {op.label}
                    </MenuItem>
                  </Box>
                ))
              )}
            </Menu>
            <Button
              variant="contained"
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={guardar}
              disabled={!puedeModificar || cargando || guardando}
              sx={btnPrimarySx}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} sx={{ color: FP.color.navy }} />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Card sx={cardShellSx}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    mb: 2.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.4,
                      bgcolor: FP.color.accentBlueSoft,
                      color: FP.color.accentBlue,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1rem' }}>
                      Registro inicial de nómina
                    </Typography>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                      Jornada laboral, horas extras, aportes y periodicidad.
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={tituloSeccionSx}>Horario laboral</Typography>
                <Grid container spacing={2}>
                  {CAMPOS_JORNADA.map((item) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={item.campo}>
                      {campoHora(item)}
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card sx={cardShellSx}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography sx={tituloSeccionSx}>Horas extras</Typography>
                <Grid container spacing={2}>
                  {CAMPOS_EXTRAS.map((item) => (
                    <Grid item xs={12} sm={6} lg={12} key={item.campo}>
                      {campoHora(item)}
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card sx={cardShellSx}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography sx={tituloSeccionSx}>Aportaciones</Typography>
                <Grid container spacing={2}>
                  {CAMPOS_APORTES.map((item) => (
                    <Grid item xs={12} sm={6} lg={12} key={item.campo}>
                      {campoPorcentaje(item)}
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={12} lg={4}>
            <Card sx={cardShellSx}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography sx={tituloSeccionSx}>Periodicidad</Typography>
                <TextField
                  select
                  fullWidth
                  label="Periodicidad"
                  helperText="Si la nómina se paga mensual o quincenal"
                  value={formulario.periodicidad}
                  onChange={setCampo('periodicidad')}
                  disabled={!puedeModificar}
                  sx={fpInputSx}
                >
                  {PERIODICIDADES.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {puedeIniciarNomina && (
        <IniciarNomina
          open={accionActiva === 'iniciarnomina'}
          onClose={() => setAccionActiva(null)}
        />
      )}
      {puedeDefinirNomina && (
        <DefinicionNomina
          open={accionActiva === 'definicionnomina'}
          onClose={() => setAccionActiva(null)}
        />
      )}
      {puedeAplicarDescuentos && (
        <AplicarDescuentos
          open={accionActiva === 'aplicardescuentos'}
          onClose={() => setAccionActiva(null)}
        />
      )}
      {puedeExcluirEmpleados && (
        <ExcluirEmpleadoNomina
          open={accionActiva === 'excluirempleados'}
          onClose={() => setAccionActiva(null)}
        />
      )}
      {puedeCerrarNomina && (
        <CerrarNomina
          open={accionActiva === 'cerrarnomina'}
          onClose={() => setAccionActiva(null)}
        />
      )}
    </Box>
  );
};

export default ConfiguracionNomina;
